import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import type { HistoryPoint } from '../intelligence';
import { series, chrome, chartFont } from '../reportTheme';
import ChartTooltip from './ChartTooltip';
import { dayLabel, dayTs, timeAxis } from './timeAxis';

const axisTick = { fill: chrome.muted, fontSize: 12, fontFamily: chartFont };

interface KpiHistoryChartProps {
  data: HistoryPoint[];
  seriesName: string;
  unit?: string;
  /** Fixes the axis for bounded KPIs (scores and percentages are 0-100). */
  max?: number;
}

/**
 * One KPI's stored history as a line (spec §11). Points come from the engine's
 * daily snapshots; a day the KPI was not computable has no point, so the line
 * is drawn with gaps rather than dipping through a zero it never had. Every
 * measured day is marked with a dot, and the time axis covers at least a week.
 */
const KpiHistoryChart: React.FC<KpiHistoryChartProps> = ({
  data,
  seriesName,
  unit = '',
  max,
}) => {
  const rows = useMemo(
    () =>
      data
        .map((point) => ({ ts: dayTs(point.date), [seriesName]: point.value }))
        .sort((a, b) => a.ts - b.ts),
    [data, seriesName],
  );
  const axis = useMemo(() => timeAxis(rows.map((row) => row.ts)), [rows]);

  const fmt = (value: number | string) => `${value}${unit}`;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
        <CartesianGrid stroke={chrome.grid} vertical={false} />
        <XAxis
          dataKey="ts"
          type="number"
          scale="time"
          domain={axis.domain}
          ticks={axis.ticks}
          tickFormatter={dayLabel}
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: chrome.axis }}
        />
        <YAxis
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          width={44}
          domain={max != null ? [0, max] : ['auto', 'auto']}
          tickFormatter={fmt}
        />
        <Tooltip
          content={<ChartTooltip valueFormatter={fmt} />}
          labelFormatter={(value) => dayLabel(Number(value))}
          cursor={{ stroke: chrome.axis, strokeWidth: 1 }}
        />
        <Line
          type="monotone"
          dataKey={seriesName}
          stroke={series.blue}
          strokeWidth={2}
          dot={{ r: 3, fill: series.blue, strokeWidth: 0 }}
          connectNulls={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: chrome.surface }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default KpiHistoryChart;
